import { LIVE_API } from './config'
import { db, commit } from './portalDb'
import { http, latency, PortalApiError } from './portalHttp'
import type { OrgMembership, PortalUser, Session } from '../types/portal'

/**
 * Authentication, following the specification:
 *
 * - Magic link only. A 64 byte random token, hex encoded; only its SHA-256 hash
 *   is stored, with a 15 minute lifetime, and it is deleted on first use.
 * - 3 link requests per email per 15 minutes.
 * - No self registration. An address that has not been invited gets the same
 *   neutral response as one that has, so the form cannot be used to discover
 *   who holds an account.
 * - A 15 minute access token refreshed silently, with a 30 day refresh token.
 *
 * In live mode every call goes to /api/v1/auth. In the preview the same rules
 * run against the in-memory store, and the link is handed back to the screen
 * because there is no inbox to send it to.
 */

const TOKEN_TTL_MS = 15 * 60 * 1000
const TOKEN_BYTES = 64
const RATE_LIMIT = 3
const RATE_WINDOW_MS = 15 * 60 * 1000
const ACCESS_TTL_MS = 15 * 60 * 1000
const REFRESH_TTL_MS = 30 * 24 * 60 * 60 * 1000
const STORAGE_KEY = 'cavalla.portal.session'

const pendingTokens = new Map<string, { hash: string; expiresAt: number }>()
const requestLog = new Map<string, number[]>()

let current: Session | null = null

export function getSession(): Session | null {
  return current
}

/** Throws UNAUTHORIZED when there is no live session, exactly as the API would. */
export function requireSession(): Session {
  if (!current || current.expiresAt < Date.now()) {
    throw new PortalApiError('UNAUTHORIZED')
  }
  return current
}

export interface MagicLinkResult {
  /** Preview only: the link that would have been emailed. Never present in live mode. */
  previewLink?: string
}

export async function requestMagicLink(rawEmail: string): Promise<MagicLinkResult> {
  const email = rawEmail.trim().toLowerCase()
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new PortalApiError('VALIDATION_ERROR', 'Please enter a valid email address.', { email: 'invalid' })
  }
  if (LIVE_API) {
    await http<void>('POST', '/auth/magic-link', { email })
    return {}
  }

  await latency(450)
  enforceRateLimit(email)

  const user = db.users.find((row) => row.email === email && !row.isInternal)
  const invited = user && db.orgMembers.some((member) => member.userId === user.id)
  if (!user || !invited) return {}

  const token = randomHex(TOKEN_BYTES)
  pendingTokens.set(email, { hash: await sha256(token), expiresAt: Date.now() + TOKEN_TTL_MS })
  return {
    previewLink: `/portal/auth/verify?token=${token}&email=${encodeURIComponent(email)}`,
  }
}

export interface VerifyResult {
  session: Session
  needsOnboarding: boolean
}

export async function verifyMagicLink(rawEmail: string, token: string): Promise<VerifyResult> {
  const email = rawEmail.trim().toLowerCase()
  if (LIVE_API) {
    const session = await http<Session>('GET', `/auth/verify?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`)
    current = session
    return { session, needsOnboarding: !session.user.firstName }
  }

  await latency(500)
  const stored = pendingTokens.get(email)
  const expired = !stored || stored.expiresAt < Date.now()
  if (expired || stored.hash !== (await sha256(token))) {
    pendingTokens.delete(email)
    throw new PortalApiError(
      'UNAUTHORIZED',
      'This sign in link has expired or has already been used. Links last 15 minutes and work once. Request a new one below.',
    )
  }
  pendingTokens.delete(email)

  const user = db.users.find((row) => row.email === email)
  if (!user) throw new PortalApiError('UNAUTHORIZED')
  user.lastLoginAt = new Date().toISOString()
  const session = buildSession(user)
  persist(user.id)
  commit()
  return { session, needsOnboarding: !user.firstName }
}

export async function completeOnboarding(input: { firstName: string; lastName: string; jobTitle: string }): Promise<Session> {
  const errors: Record<string, string> = {}
  if (!input.firstName.trim()) errors.firstName = 'Please enter your first name.'
  if (!input.lastName.trim()) errors.lastName = 'Please enter your last name.'
  if (!input.jobTitle.trim()) errors.jobTitle = 'Please enter your job title.'
  if (Object.keys(errors).length) throw new PortalApiError('VALIDATION_ERROR', undefined, errors)

  if (LIVE_API) {
    const session = await http<Session>('PATCH', '/auth/me', input)
    current = session
    return session
  }

  await latency()
  const session = requireSession()
  const user = db.users.find((row) => row.id === session.user.id)
  if (!user) throw new PortalApiError('UNAUTHORIZED')
  user.firstName = input.firstName.trim()
  user.lastName = input.lastName.trim()
  user.jobTitle = input.jobTitle.trim()
  commit()
  return buildSession(user)
}

/** Restores a session on load. Returns null rather than throwing when signed out. */
export async function me(): Promise<Session | null> {
  if (LIVE_API) {
    try {
      current = await http<Session>('GET', '/auth/me')
      return current
    } catch {
      current = null
      return null
    }
  }
  const stored = readStored()
  if (!stored) return null
  const user = db.users.find((row) => row.id === stored.userId)
  return user ? buildSession(user) : null
}

/** Called silently before the access token expires. */
export async function refresh(): Promise<Session | null> {
  if (LIVE_API) {
    try {
      await http<void>('POST', '/auth/refresh')
      return me()
    } catch {
      current = null
      return null
    }
  }
  const stored = readStored()
  if (!stored || !current) return null
  const user = db.users.find((row) => row.id === stored.userId)
  return user ? buildSession(user) : null
}

export async function logout(): Promise<void> {
  current = null
  if (LIVE_API) {
    await http<void>('POST', '/auth/logout').catch(() => undefined)
    return
  }
  window.sessionStorage.removeItem(STORAGE_KEY)
}

// ─── Internals ────────────────────────────────────────────────────────────

export function membershipsFor(userId: string): OrgMembership[] {
  return db.orgMembers
    .filter((member) => member.userId === userId)
    .map((member) => ({
      orgId: member.orgId,
      role: member.role,
      productIds: db.organisations.find((org) => org.id === member.orgId)?.productIds ?? [],
    }))
}

function buildSession(user: PortalUser): Session {
  current = {
    user: { ...user },
    orgMemberships: membershipsFor(user.id),
    expiresAt: Date.now() + ACCESS_TTL_MS,
  }
  return current
}

function enforceRateLimit(email: string): void {
  const now = Date.now()
  const recent = (requestLog.get(email) ?? []).filter((at) => now - at < RATE_WINDOW_MS)
  if (recent.length >= RATE_LIMIT) {
    const waitMinutes = Math.ceil((RATE_WINDOW_MS - (now - recent[0])) / 60_000)
    throw new PortalApiError(
      'RATE_LIMITED',
      `We have sent three links to this address in the last 15 minutes. Please use the most recent one, or try again in ${waitMinutes} minute${waitMinutes === 1 ? '' : 's'}.`,
    )
  }
  recent.push(now)
  requestLog.set(email, recent)
}

function persist(userId: string): void {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, refreshExpiresAt: Date.now() + REFRESH_TTL_MS }))
}

function readStored(): { userId: string } | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { userId: string; refreshExpiresAt: number }
    if (parsed.refreshExpiresAt < Date.now()) return null
    return parsed
  } catch {
    return null
  }
}

function randomHex(bytes: number): string {
  const buffer = new Uint8Array(bytes)
  window.crypto.getRandomValues(buffer)
  return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function sha256(value: string): Promise<string> {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}
